import { Capacitor } from "@capacitor/core";
import { Keyboard } from '@capacitor/keyboard';
import { Trans, msg, t } from "@lingui/macro";
import { RecordingData } from "capacitor-voice-recorder";
import EventEmitter, { on } from "events";
import { CheckCircle2, Send, Smile, X } from "lucide-react";
import { useEffect, useReducer, useState, useContext } from "react";
import { SuggestionDataItem } from "react-mentions";
import { FileAttachment, Message, SendFile } from "../../../models/Message";
import { Room } from "../../../models/Room";
import { EmojiType } from "../../../models/local/Types";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { ResizeImage } from "../../../services/Utils/ImageResizer";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { AppSettings } from "../../../const/Settings";
import MessageTextArea from "../MessageTextArea";
import { PlayMessageButton } from "../PlayMessageButton";
import ProfileImg from "../ProfileImg";
import { UIMessage } from "../UIMessage";
import MsgAddMorePopover, { UploadFilesContext } from "./MsgAddMorePopover";
import MsgEmojiSelector from "./MsgEmojiSelector";
import MsgInputFooterReactions from "./MsgInputFooterReactions";
import MsgRecordModal, { RecordingAction } from "./MsgRecordModal";
import { RoomItem } from "../../../models/RoomItem";
import InputAddedItemList from "./InputAddedItemList";
import { Util } from "../../../services/Utils/Util";
import { useAppUIContext } from "../../../context/AppUIContext";
import { FileIcon } from "../MessageFileItem";
import { UploadTask } from "firebase/storage";
import { Avatar, AvatarImage, AvatarFallback } from "../../../components/ui/avatar";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Progress } from "../../../components/ui/progress";
import { useToast } from "../../../components/ui/use-toast";
import { cn } from "../../../lib/utils";

const MsgInputFooterEmitter = new EventEmitter();
interface IMsgInputFooter { room: Room, parentMessage: UIMessage | null, messages: UIMessage[], onClearParent: (arg0?: UIMessage) => void, onScrollToParent?: () => void, onProgressForFile: (key: string, sendFile: SendFile) => void, onSendedMessage: (arg0: UIMessage) => void };

const MsgInputFooter: React.FC<IMsgInputFooter> = ({ room, parentMessage, messages, onClearParent, onScrollToParent, onProgressForFile, onSendedMessage }) => {
  const { soribots } = useAppUIContext();
  const [hasText, setHasText] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [showEmojiSelctor, setShowEmojiSelector] = useState(false);
  const { toast } = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<RoomItem[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestionDataItem[]>([]);
  const [message, setMessage] = useState(new Message(room, My.user));
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [notifyOnlyMentioned, setNotifyOnlyMentioned] = useState(false);
  const [isMentioning, setIsMentioning] = useState(false);
  const [progress, setProgress] = useState<{ [key: string]: number }>({});
  const [uploadTask, setUploadTask] = useState<{ [key: string]: UploadTask }>({});
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener('keyboardDidShow', () => {
        setIsKeyboardOpen(true);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyboardOpen(false);
      });
    }

    MsgInputFooterEmitter.on('paste', (file) => {
      setFiles(p => p.concat(file));
    });
    return () => {
      MsgInputFooterEmitter.removeAllListeners();
      if (Capacitor.isNativePlatform()) {
        Keyboard.removeAllListeners();
      }
    };
  }, [])

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles: File[] = [];
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        const file = e.dataTransfer.files[i];
        if (!files.find(f => f.name === file.name)) {
          newFiles.push(file);
        }
      }
      if (newFiles.length > 0) {
        setFiles([...newFiles, ...files]);
      }
    }
  }

  useEffect(() => {
    setMessage(new Message(room, My.user));
    setNotifyOnlyMentioned(false);
    setHasText(false);
    setIsMentioning(false);
    setSuggestions(getMentionSuggestions())
  }, [room.id]);

  useEffect(() => {
    setSuggestions(getMentionSuggestions())
  }, [room]);

  useEffect(() => {
    message.replyTo = parentMessage?.msg.user;
    message.replyToMessageId = parentMessage?.msg.id ?? null;
    message.parentMessageId = parentMessage?.msg.parentMessageId ?? parentMessage?.msg.id ?? null;
    forceUpdate();
  }, [parentMessage]);

  return (
    <div
      id="msg-input-footer-container"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn("bg-white md:rounded-b-panel transition-all z-20 relative", isDragging && "relative")}
      style={isDragging ? { cursor: 'copy' } : {}}
    >
      {isDragging && (
        <div className="absolute inset-0 bg-[rgba(255,135,9,0.08)] border-2 border-dashed border-[#FF8709] flex items-center justify-center text-xl text-[#FF8709] z-[1000] pointer-events-none rounded-b-panel">
          <Trans>Drop files here to attach</Trans>
        </div>
      )}

      {/* Top Context Section - Replying to 포함 */}
      <div className="flex flex-col md:px-4 empty:hidden">
        {/* 답글 대상 메시지 표시 - 입력창과 동일한 스타일 */}
        {parentMessage && (
          <div className="flex items-center gap-3 py-2.5 px-4 bg-orange-50 border-y md:border border-orange-200 md:rounded-[24px] mb-0 min-h-[48px]">
            <div
              className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => onScrollToParent?.()}
            >
              <div className="text-xs font-semibold text-orange-600">
                <Trans>Replying to</Trans> {parentMessage.msg.user.name}
              </div>
              <div
                className="text-sm text-gray-600 truncate"
                dangerouslySetInnerHTML={{ __html: parentMessage.msg.text ? UtilApp.getTextAsHTML(parentMessage.msg.text, My.user.id) : t`Attachment` }}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full hover:bg-orange-100 shrink-0"
              onClick={() => onClearParent()}
            >
              <X className="h-4 w-4 text-orange-500" />
            </Button>
          </div>
        )}
        <ShowEmojis />
        <ShowRecording />
        {files.length > 0 && <div className="flex gap-2.5 overflow-x-auto py-2 custom-scrollbar">{files.map((file, i) => getFileItem(file, i))}</div>}
        <InputAddedItemList />
      </div>
      {/* Unified Input Container (Figma Style) */}
      <div className={cn(
        "flex flex-col w-full md:border border-[#E5E5E5] md:rounded-[24px] transition-all mt-2",
        "bg-white focus-within:border-orange-500 focus-within:ring-4 focus-within:ring-orange-50/50"
      )}>
        {inProgress && <Progress value={undefined} className="h-0.5 w-full" />}

        {/* 모바일: 2줄 레이아웃 (텍스트 위, 버튼 아래) / 데스크톱: 1줄 레이아웃 */}
        <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">

          {/* 텍스트 입력 - 모바일에서 위쪽, 라운드 보더 */}
          {/* 댓글 작성 시(parentMessage) 오렌지 테마 적용 */}
          <div className={cn(
            "flex-1 min-h-[30px] pl-3 pr-0 mx-2 mt-2 md:mt-0 md:mx-0 md:px-0 order-1 md:order-2 border md:border-0 rounded-[20px] md:rounded-none transition-colors",
            parentMessage
              ? "border-orange-300 bg-orange-50 md:bg-transparent"
              : "border-gray-200 bg-gray-50 md:bg-transparent"
          )}>
            <MessageTextArea message={message} suggestions={suggestions} onChange={onInputTextChange} onSubmit={submit} />
          </div>

          {/* 버튼 행 - 모바일에서 아래쪽 */}
          <div className="flex items-center justify-between order-2 md:order-1 md:contents mt-0 px-2 py-1 md:p-0">
            {/* Left: Plus & Emoji Buttons */}
            <div className="flex items-center gap-0.5 md:gap-1 shrink-0 md:pl-2">
              <UploadFilesContext.Provider value={{ files, setFiles, items, setItems }}>
                <MsgAddMorePopover room={room} parentMessage={parentMessage?.msg} />
              </UploadFilesContext.Provider>
              {AddEmojiButton()}
            </div>

            {/* Right: Record/Send Button */}
            <div className="flex items-center shrink-0 md:pr-2 md:order-3">
              <div className="relative flex items-center justify-center">
                {shoudShowSendButton() ? (
                  <span onClick={submit} className="cursor-pointer flex items-center justify-center w-8 h-8 rounded-full bg-[#FF8709] hover:bg-[#e67808] transition-colors shadow-sm" data-cy="send">
                    <Send className="h-4 w-4 text-white" />
                  </span>
                ) : (
                  <div className="flex items-center justify-center w-8 h-8">
                    <MsgRecordModal onAction={handleRecordingAction} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notify Checkbox */}
        <div className="px-4 pb-2 empty:hidden">
          {NotifyOnlyMentionedPart()}
        </div>
      </div>

      <MsgEmojiSelector show={showEmojiSelctor} setShow={setShowEmojiSelector} onSelect={handleEmojiSelected} />
    </div>
  )
  function getFileItem(file: File, index: number) {
    const removeBtn = (
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/80 hover:bg-white shadow-sm z-10"
        onClick={() => removeFile(index, file)}
      >
        <X className="h-4 w-4 text-gray-600" />
      </Button>
    );
    let fileKey = `${file.name}/${index}`;

    return (
      <div key={index} className="relative inline-flex flex-col shrink-0 bg-white rounded-lg border border-[#e5e5e5] overflow-hidden">
        {file.type.includes('image') ? (
          <div className="relative h-24 w-24">
            <img src={URL.createObjectURL(file)} title={file.name} className="h-full w-full object-cover" alt={file.name} />
            {removeBtn}
          </div>
        ) : (
          <div className="relative flex flex-col items-center justify-center p-3 min-w-[100px] h-24">
            <div className="mb-2">
              <FileIcon filename={file.name} />
            </div>
            {progress[fileKey] && <Progress value={progress[fileKey] * 100} className="absolute bottom-1 left-0 right-0 h-1" />}
            {removeBtn}
            <p className="text-xs text-center truncate max-w-[120px] px-1 text-gray-700 mt-1">{file.name}</p>
            <p className="text-[10px] text-gray-500">{Util.niceBytes(file.size)}</p>
          </div>
        )}
      </div>
    );
  }

  function removeFile(index: number, file: any) {
    let fileKey = `${file.name}/${index}`;
    setProgress((prevProgress) => {
      const newProgress = { ...prevProgress };
      delete newProgress[fileKey];
      return newProgress;
    });
    if (uploadTask[fileKey] != null) {
      uploadTask[fileKey].cancel();
      setUploadTask((prevUploadTask) => {
        const newUploadTask = { ...prevUploadTask };
        delete newUploadTask[fileKey];
        return newUploadTask;
      });
    }
    setFiles(files.filter(i => i != file));
  }


  function handleRecordingAction(action: RecordingAction, data: RecordingData | undefined) {
    if (message) {
      message.recording = data;
      forceUpdate();
      if (action === 'send') {
        submit();
      }
    }
  }

  function ShowRecording() {
    if (message.recording) {
      return (
        <div className="relative border-t border-[#e5e5e5] pt-2">
          <PlayMessageButton m={new UIMessage(message, [])} />
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-[90px] top-5 h-6 w-6 z-10"
            onClick={clearRecording}
          >
            <X className="h-4 w-4 text-gray-500" />
          </Button>
        </div>
      );
    }
    else
      return <></>
  }

  function ShowEmojis() {
    if (message.emoji) {
      return (
        <div className="relative border-t border-[#e5e5e5] pt-2">
          <img src={`assets/emoji/${message.emoji}.png`} className="mt-2 ml-2 w-24 rounded-lg" alt="emoji" />
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-[90px] top-5 h-6 w-6 z-10"
            onClick={clearEmoji}
          >
            <X className="h-4 w-4 text-gray-500" />
          </Button>
        </div>
      );
    }
    else
      return <></>
  }

  function AddEmojiButton() {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-full bg-[#f2f2f2] hover:bg-[#e5e5e5] text-[#939393] hover:text-[#555] hover:bg-gray-100 transition-colors"
        onClick={() => {
          setShowEmojiSelector(true);
          focusTextAreaIfKeyboardIsOpen();
        }}
      >
        <Smile className="h-5 w-5" />
      </Button>
    );
  }

  function clearRecording() {
    message.recording = undefined;
    forceUpdate();
  }

  function clearEmoji() {
    message.emoji = undefined;
    forceUpdate();
  }

  function onInputTextChange(value: string) {
    setHasText(value.length > 0);
    setIsMentioning(value.indexOf('@[') > -1);
  }

  function shoudShowSendButton(): boolean {
    return hasText || files.length > 0 || !!message.recording || !!message.emoji;
  }

  async function submit() {
    const msgToSend = message;
    let _room = My.Rooms.get(room.id) ?? room; // Get latest version of the room.
    msgToSend.userIds = _room.userIds; // Set the latest userIds. Room may gotten new members.
    const uimsg = new UIMessage(msgToSend, []);
    uimsg.sendFiles = files;
    uimsg.isLocal = true;
    setHasText(false);
    setMessage(new Message(_room, My.user));
    setIsMentioning(false);
    onClearParent(uimsg);

    if (files.length > 0) {
      const fileAttachments = await uploadFilesForMsg(msgToSend.id, files);
      if (fileAttachments !== null) {
        msgToSend.files = fileAttachments;
      } else {
        toast({
          title: t`Failed to send message. Please try again later.`,
        });
        setMessage(msgToSend);
        return;
      }
    }

    if (items.length > 0) {
      items.forEach(item => {
        if (item.url && item.title) {
          const f = new FileAttachment(item.title, item.url, 0, Util.isImageUrl(item.url) ? 'image' : 'doc', "");
          f.isCopy = true;
          msgToSend.files.push(f);
        }
      });
      setItems([]);
    }

    try {
      await Fire.msg.saveMessageAndNotify(msgToSend, notifyOnlyMentioned);
      onSendedMessage(uimsg);
    } catch (error) {
      console.error(error);
      toast({
        title: t`Failed to send message. Please try again later.`,
      });
    } finally {
      setInProgress(false);
    }

    Fire.bot.askSoribotIfMentioned(msgToSend, soribots, messages);
  }


  async function uploadFilesForMsg(msgId: string, files: File[]): Promise<FileAttachment[] | null> {
    let fileAttachments: FileAttachment[] = [];
    setFiles([]);
    if (files.length == 0) {
      return fileAttachments;
    } else {
      return new Promise(async (resolve, reject) => {
        for (let index = 0; index < files.length; index++) {
          let file = files[index];
          const filename = (file.name.startsWith('image.')) ? `${index + 1}-${file.name}` : file.name;
          let fileKey = `${msgId}/${file.name}`;
          const group = file.type.includes('image') ? 'image' : 'doc';
          const sendFile = new SendFile(file, room.id);
          sendFile.msgId = msgId;
          sendFile.index = index;
          if (AppSettings.IMAGE_RESIZING_ON_UPLOAD_ENABLED && group == 'image' && file.size > 100000) {
            file = await ResizeImage(file, 1000);
          }

          Fire.uploadFileOptions(
            file,
            `/rooms/${room.id}/${msgId}/${filename}`,
            {
              onTask: (task) => {
                sendFile.uploadTask = task;
                setUploadTask((prevUploadTask) => ({
                  ...prevUploadTask,
                  [fileKey]: task,
                }));
              },
              onComplete: (url) => {
                if (url) {
                  const fileAttachment = new FileAttachment(file.name, url, file.size, group, "");
                  fileAttachments.push(fileAttachment);
                  if (fileAttachments.length === files.length) {
                    resolve(fileAttachments);
                  }
                } else {
                  resolve(null);
                }
              },
              onProgress: (percent) => {
                sendFile.progress = percent / 100;
                onProgressForFile(fileKey, sendFile);
                setProgress((prevProgress) => ({
                  ...prevProgress,
                  [fileKey]: percent / 100,
                }));
              },
              onStateChange: (state) => {
                switch (state) {
                  case 'paused':
                  case 'running':
                    break;
                }

                files.forEach((f, i) => {
                  if (f === file) {
                    files.splice(i, 1);
                  }
                });
                if (fileAttachments.length === files.length) {
                  resolve(fileAttachments);
                }
              }
            }
          )
        }
      });
    }
  }

  function handleEmojiSelected(emoji: EmojiType) {
    message.emoji = emoji;
    focusTextAreaIfKeyboardIsOpen();
    forceUpdate();
  }

  function getMentionSuggestions(): SuggestionDataItem[] {
    const list: SuggestionDataItem[] = []

    // Add Everyone option
    list.push({ id: 'everyone', display: 'everyone', avatar: '/assets/icon/favicon.png' } as any);

    room.users.forEach(u => {
      list.push({ id: u.id, display: u.name, avatar: u.photoUrl } as any);
    });
    room.bots?.forEach(b => {
      list.push({ id: b.id ?? b.type, display: b.name, avatar: (b as any).photoUrl } as any);
    });
    soribots?.filter(b => b.roomIds?.includes(room.id)).forEach(b => {
      list.push({ id: `${b.type}-${b.id}`, display: b.name, avatar: (b as any).photoUrl } as any);
    });
    return list;
  }

  function focusTextAreaIfKeyboardIsOpen() {
    if (isKeyboardOpen) {
      const textarea = document.querySelector('#msg-input-footer-container .mention-textarea textarea') as HTMLTextAreaElement;
      textarea.focus();
    }
  }

  function NotifyOnlyMentionedPart() {
    if (!parentMessage && isMentioning)
      return (
        <div className="flex items-center gap-2 p-2 bg-[#f2f2f2] text-sm rounded-lg border border-dashed border-[#ddd]">
          <Checkbox
            id="notify-mentioned"
            checked={notifyOnlyMentioned}
            onCheckedChange={() => setNotifyOnlyMentioned(p => !p)}
          />
          <label htmlFor="notify-mentioned" className="cursor-pointer select-none">
            <Trans>Notify only mentioned users</Trans>
          </label>
        </div>
      );
    else
      return null;
  }
}

export default MsgInputFooter;

function pasteImageHandler(evt: ClipboardEvent) {
  if (evt.clipboardData) {
    const items: DataTransferItem[] = [].slice.call(evt.clipboardData.items).filter((item: DataTransferItem) => {
      return item.type.indexOf('image') !== -1;
    });

    if (items.length === 0) {
      return;
    } else {
      let file = items[0].getAsFile();
      if (file) {
        MsgInputFooterEmitter.emit('paste', file);
      }
    }
  }
}

document.addEventListener('paste', pasteImageHandler);
